/**
 * useDebateHistory - Hook for fetching and managing past debate records.
 */

import { useState, useCallback } from 'react';
import { API_BASE_URL } from '@/lib/api';

export interface DebateSummary {
  id: string;
  question: string;
  depth: string;
  mode: string;
  topic_type: string;
  tickers: string[];
  agent_count: number;
  rounds_completed: number;
  agreement_score: number;
  created_at: string;
}

export interface DebateDetail {
  id: string;
  question: string;
  depth: string;
  mode: string;
  topic_type: string;
  tickers: string[];
  agents: Array<{ id: string; role: string; icon: string }>;
  rounds: Array<Record<string, unknown>>;
  consensus: Record<string, unknown>;
  agreement_score: number;
  data_sources: string[];
  reference_debate_id: string | null;
  credits_consumed: number;
  total_time_seconds: number;
  created_at: string;
}

export function useDebateHistory() {
  const [debates, setDebates] = useState<DebateSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDebates = useCallback(async (limit = 20, offset = 0) => {
    setIsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/debate/history?limit=${limit}&offset=${offset}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setDebates(data);
      return data;
    } catch (e) {
      const msg = (e as Error).message || 'Failed to fetch debate history';
      setError(msg);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getDebateDetail = useCallback(async (debateId: string): Promise<DebateDetail | null> => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/v1/debate/${debateId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }, []);

  return {
    debates,
    isLoading,
    error,
    fetchDebates,
    getDebateDetail,
  };
}
